import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Accordions } from '@startengine/design-system.ui.accordions';
import { Button } from '@startengine/design-system.ui.button';
import { Divider } from '@startengine/design-system.ui.divider';
import { ChevronDownIcon, RightArrowIcon } from '@startengine/design-system.ui.icons';
import { colors } from '@startengine/design-system.theme.main';

import { useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import NoSsr from '@mui/material/NoSsr';
import { getOfferingFAQList } from './utils';
import { imagePath } from 'utils/helpers';
import { isAmountBasedSecurity } from 'utils/offerings/helpers';
import useIsMobile from 'hooks/useIsMobile';

const getCancellationText = (isRegD, isRegA) => {
  if (isRegD) {
    return null;
  }
  if (isRegA) {
    return 'Cancel within 48 hours of your investment commitment.';
  }
  return 'Cancel anytime before 48 hours before a rolling close or the offering end date.';
};

export const StepsToInvest = ({ isMobile, isRegD, isRegA }) => {
  const cancellationText = getCancellationText(isRegD, isRegA);

  return (
    <Grid container mt={4} data-cy="how-investing-works-section" position="relative">
      <Grid item xs={12}>
        <Typography variant={isMobile ? 'h5Caps' : 'h4'}>HOW INVESTING WORKS</Typography>
      </Grid>
      <Grid id="anchor-faq" position="absolute" top="-85px" />
      {cancellationText && (
        <Grid item xs={12}>
          <Typography color={colors.SEDark} variant="p1Medium" fontStyle="italic">
            {cancellationText}
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} mt="62px">
        {isMobile ? (
          <Image
            src={`${imagePath}/images/offering/faq-timeline-vertical.svg`}
            alt="FAQ Timeline Vertical"
            width="309"
            height="340"
          />
        ) : (
          <Image
            src={`${imagePath}/images/offering/faq-timeline.svg`}
            alt="FAQ Timeline"
            width="772"
            height="112"
          />
        )}
      </Grid>
      <Grid item xs={12}>
        {!isMobile && <Divider />}
      </Grid>
    </Grid>
  );
};

export const WhyStartEngine = ({ isMobile }) => {
  return (
    <Box
      sx={{
        backgroundColor: colors.SEMidnight,
        borderRadius: 4,
      }}
      px="24px"
      py={4}
      mb={11.5}
      mt={5}
      data-cy="why-startengine-section"
    >
      <Grid container alignItems="center">
        <Grid item xs={12} mb={2}>
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            color="secondary.main"
            whiteSpace="nowrap"
            letterSpacing="2px"
          >
            WHY STARTENGINE?
          </Typography>
        </Grid>
        <Grid item container flexWrap="nowrap" mb={2}>
          <Image
            src={`${imagePath}/images/icons/rewards-icon.svg`}
            alt="Rewards Icon"
            height={40}
            width={40}
          />
          <Grid item container ml={2} direction="column">
            <Typography variant="h8" color="secondary.main" letterSpacing="2px">
              REWARDS
            </Typography>
            <Typography
              variant="p2Medium"
              color="common.tabsBackground"
              sx={{
                maxWidth: 1,
              }}
            >
              We want you to succeed and get the most out of your money by offering rewards and
              memberships!
            </Typography>
          </Grid>
        </Grid>
        <Grid item container flexWrap="nowrap" mb={2}>
          <Image
            src={`${imagePath}/images/icons/lock-icon.svg`}
            alt="Lock Icon"
            height={40}
            width={40}
          />
          <Grid item container ml={2} direction="column">
            <Typography variant="h8" color="secondary.main" letterSpacing="2px">
              SECURE
            </Typography>
            <Typography variant="p2Medium" color="common.tabsBackground">
              Your info is your info. We take pride in keeping it that way!
            </Typography>
          </Grid>
        </Grid>
        <Grid item container flexWrap="nowrap" mb={2}>
          <Image
            src={`${imagePath}/images/icons/ellipse-icon.svg`}
            alt="Ellipse Icon"
            height={40}
            width={40}
          />
          <Grid item container direction="column" ml={2}>
            <Typography variant="h8" color="secondary.main" letterSpacing="2px">
              DIVERSE INVESTMENTS
            </Typography>
            <Typography variant="p2Medium" color="common.tabsBackground">
              Invest in over 200 start-ups and collectibles!
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export const FAQAccordion = ({ isTTW, isRegD, isRegA, isAmountBased = false }) => {
  const campaignFAQs = getOfferingFAQList({ isTTW, isRegD, isRegA, isAmountBased });

  const campaignFAQList = campaignFAQs.map((item, index) => (
    <div key={`faq-box-${item.id}`} data-cy="faq-section">
      <Box
        sx={{
          width: '100%',
        }}
      >
        <NoSsr>
          <Accordions
            key={`faq-${item.id}`}
            summary={`${item?.title}` || ''}
            details={item?.detail}
            expandIcon={
              <Button
                icon
                aria-label={`Toggle answer for ${item?.title || 'FAQ'}`}
                sx={{
                  padding: 0,
                  height: '36px',
                  width: '36px',
                }}
                data-cy={`faq-${item?.title}-accordion-button`}
              >
                <ChevronDownIcon
                  aria-hidden="true"
                  focusable="false"
                  sx={{
                    height: '100%',
                    width: '100%',
                  }}
                />
              </Button>
            }
            sx={{
              '&::before': {
                backgroundColor: 'transparent',
              },
            }}
          />
        </NoSsr>
      </Box>
      {index === campaignFAQs.length - 1 ? null : (
        <Box
          key={`faq-box-divider-${item.id}`}
          sx={{
            width: '100%',
            px: {
              xs: 0,
              lg: 2,
            },
          }}
        >
          <Divider />
        </Box>
      )}
    </div>
  ));

  return <div>{campaignFAQList}</div>;
};

export const SeeMoreFAQ = () => {
  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <a
        style={{
          textDecoration: 'none',
        }}
        href="https://www.startengine.com/investor-faq"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="More FAQs"
      >
        <Accordions
          key="faq-accord-seemore"
          summary="More FAQs"
          expandIcon={
            <Button
              icon
              aria-label="Open more FAQs"
              sx={{
                padding: 0,
                height: '36px',
                width: '36px',
              }}
              data-cy="faq-more-button"
            >
              <RightArrowIcon
                aria-hidden="true"
                focusable="false"
                sx={{
                  height: '100%',
                  width: '100%',
                }}
              />
            </Button>
          }
          onChange={() => false}
          sx={{
            '&::before': {
              backgroundColor: 'transparent',
            },
          }}
        />
      </a>
    </Box>
  );
};

export const FAQ = () => {
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData(['offeringData']);
  const isRegD = data?.regulationType === 'Reg D' || data?.regulationType === '506c';
  const isRegA = data?.regulationType === 'Reg A+' || data?.regulationType === 'Reg A';
  const isTTW = data?.status === 'testing';
  const isAmountBased = isAmountBasedSecurity(data);
  const isMobile = useIsMobile();

  return (
    <>
      {!isTTW && <StepsToInvest isMobile={isMobile} isRegD={isRegD} isRegA={isRegA} />}
      <WhyStartEngine isMobile={isMobile} />
      <Typography variant="h5" color="success" mb={2} ml={2}>
        FAQS
      </Typography>
      <Box mb={2} />
      <FAQAccordion isTTW={isTTW} isRegD={isRegD} isRegA={isRegA} isAmountBased={isAmountBased} />
      {!isTTW && <SeeMoreFAQ />}
    </>
  );
};

export default FAQ;
